import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';
import { buildMapEmbed } from '@/lib/companyInfo';

export default function LocationsSection() {
  const { t } = useTranslation('contact');
  const { info } = useCompanyInfo();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const offices = info.locations.map((loc) => ({
    key: loc.id,
    badge:
      loc.type === 'hq' ? t('locations.hq.badge') : t('locations.branch.badge'),
    name: loc.name,
    address: loc.address,
    note: loc.note,
    icon: loc.type === 'hq' ? 'ri-map-pin-2-fill' : 'ri-building-2-fill',
    embed: buildMapEmbed(loc.address),
  }));

  return (
    <section className="w-full bg-cream-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block text-xs font-semibold text-warm-500 uppercase tracking-[0.2em] mb-3">
              {t('locations.label')}
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink-800 mb-3">
              {t('locations.title')}
            </h2>
            <p className="text-sm md:text-base text-ink-500 max-w-2xl mx-auto leading-relaxed">
              {t('locations.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {offices.map((office, idx) => (
              <div
                key={office.key}
                style={{ transitionDelay: `${idx * 80}ms` }}
                className={`bg-white rounded-xl border border-cream-200 overflow-hidden transition-all duration-500 ${
                  mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                <div className="p-5 md:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 bg-cream-100 flex items-center justify-center rounded-lg">
                        <i className={`${office.icon} text-warm-500 text-lg`} />
                      </div>
                      <div>
                        <span className="inline-block text-[11px] font-semibold text-warm-600 bg-warm-50 px-2 py-0.5 rounded-full mb-1">
                          {office.badge}
                        </span>
                        <h3 className="text-sm md:text-base font-bold text-ink-800">
                          {office.name}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-ink-600 leading-relaxed mb-2">{office.address}</p>
                  <p className="text-xs text-ink-400 mb-4">{office.note}</p>
                </div>
                <div className="relative w-full h-52 md:h-56 bg-cream-100">
                  <iframe
                    title={`${office.name} map`}
                    src={office.embed}
                    className="absolute inset-0 w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}